import React from 'react';
import { Box, Typography, Radio } from '@mui/material';
import TemplatePreview from '../../WidgetPreview/TemplatePreview';
import { TEMPLATE_META } from '../../TemplateSelector/templateMeta';
import { PARNAS_TEMPLATES } from '../../../business/ParnasWidget/templates/index';

const navy = '#1e3a5f';

const getTemplateMeta = (componentKey, templateName) => {
  if (componentKey === 'ParnasWidget') {
    const pt = PARNAS_TEMPLATES.find(t => t.id === templateName);
    if (pt) return { label: pt.label, desc: pt.description };
  }
  return TEMPLATE_META[componentKey]?.[templateName] || null;
};

/**
 * TemplateSplitStep
 * שלב בחירת תבנית — split-view: רשימת תבניות מימין + תצוגה מקדימה חיה משמאל
 *
 * Props:
 *   componentKey       string
 *   templates          [{ id, name, label?, description? }]
 *   selectedTemplateId string
 *   onSelectTemplate   (id) => void
 *   formData           object  (שמור לשימוש עתידי)
 */
const TemplateSplitStep = ({ componentKey, templates = [], selectedTemplateId, onSelectTemplate, formData = {}, onChange }) => {
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) || templates[0];
  const templateName = selectedTemplate?.name || '';

  return (
    <Box dir="rtl" sx={{ display: 'flex', gap: 2, minHeight: 400 }}>

      {/* Right — template list (40%) */}
      <Box sx={{
        width: '40%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        overflowY: 'auto',
        maxHeight: 420,
        pr: 0.5,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { background: '#e5e7eb', borderRadius: 2 },
      }}>
        <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.875rem', mb: 0.5 }}>
          בחר תבנית תצוגה
        </Typography>

        {templates.map((tpl) => {
          const isSelected = tpl.id === selectedTemplateId;
          const meta = getTemplateMeta(componentKey, tpl.name);
          const label = meta?.label || tpl.label || tpl.name;
          const desc  = meta?.desc  || tpl.description || null;
          return (
            <Box
              key={tpl.id}
              onClick={() => onSelectTemplate(tpl.id)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1.5,
                borderRadius: '10px',
                border: isSelected ? `2px solid ${navy}` : '1.5px solid #e5e7eb',
                bgcolor: isSelected ? '#f0f4fa' : 'white',
                cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: isSelected ? `0 0 0 3px rgba(30,58,95,0.07)` : 'none',
                '&:hover': {
                  borderColor: isSelected ? navy : '#9ca3af',
                  bgcolor: isSelected ? '#f0f4fa' : '#f9fafb',
                },
              }}
            >
              <Radio
                checked={isSelected}
                onChange={() => onSelectTemplate(tpl.id)}
                size="small"
                sx={{
                  p: 0,
                  mt: '1px',
                  color: '#d1d5db',
                  '&.Mui-checked': { color: navy },
                }}
              />
              <Box>
                <Typography sx={{
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? navy : '#111827',
                  fontSize: '0.85rem',
                  lineHeight: 1.3,
                }}>
                  {label}
                </Typography>
                {desc && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25, lineHeight: 1.4 }}>
                    {desc}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

      </Box>

      {/* Divider */}
      <Box sx={{ width: '1px', bgcolor: '#f0f0f0', flexShrink: 0 }} />

      {/* Left — live preview (60%) */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
      }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.78rem', mb: 0.5 }}>
          תצוגה מקדימה
        </Typography>
        <Box sx={{
          width: '100%',
          height: 360,
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          bgcolor: '#f8fafc',
        }}>
          {componentKey && templateName ? (
            <TemplatePreview
              componentKey={componentKey}
              templateName={templateName}
              scale={1}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '0.8rem' }}>
              בחר תבנית לתצוגה מקדימה
            </Box>
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default TemplateSplitStep;
